import { computed, Signal, WritableSignal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { IntlMessageFormat } from 'intl-messageformat';
import { PrimitiveType } from 'intl-messageformat/src/formatters';
import { distinctUntilChanged, switchMap } from 'rxjs';
import { startWith } from 'rxjs/operators';

import { KeyPathsOfType, Translation, TranslationValue } from './translation';
import { TranslationLoader } from './translation-loader';
import { UseI18n } from './use-i18n';

/**
 * An interceptor for I18n.
 *
 * The method notifyTranslated is called whenever a translation is requested.
 * It is not recommended to use this outside tests, as it will slow down the translation process.
 */
export interface I18nInterceptor {
  notifyTranslated(query: string, missing: boolean): void;
}

/**
 * An abstract I18n service.
 *
 * This can be implemented as in the example below,
 * by extending this class and providing values to the <code>super()</code> constructor.
 * The TranslationLoaderService must implement the TranslationLoader interface.
 * The method <code>useI18n()</code> is optional, but makes integrating the service easier:
 * <code>readonly i18n = useI18n();</code> can then be used directly in the applicable service/component.
 *
 * The active language signal can be a writable signal if the active language can be changed directly,
 * or a readonly signal if the subclass should handle the language changes.
 *
 * @example with a writable language signal:
 * ```ts
 * @Injectable({
 *   providedIn: 'root',
 * })
 * export class I18nService extends I18n<TranslationsFile> {
 *   constructor() {
 *     super(inject(TranslationLoaderService), signal('en-GB'));
 *   }
 * }
 *
 * export function useI18n() {
 *   return inject(I18nService).use();
 * }
 * ```
 *
 * @example Or with a computed language:
 *
 * ```ts
 * @Injectable({
 *   providedIn: 'root',
 * })
 * export class I18nService extends I18n<TranslationsFile, Signal<string>> {
 *   constructor() {
 *     super(inject(TranslationLoaderService), computed(() => inject(UserService).user().countryCode));
 *   }
 * }
 *
 * export function useI18n() {
 *   return inject(I18nService).use();
 * }
 * ```
 *
 * @template T The type of the translations object.
 * @template S The type of the active language signal:
 *             by default (when omitted), it is writable,
 *             but can be set to `Signal<string>` to prevent calling `i18n.activeLanguage.set()` directly.
 */
export abstract class I18n<T extends Translation, S extends Signal<string> = WritableSignal<string>> {
  /**
   *
   * @param loader The class used for loading translations, returning an observable of the translations object.
   * @param activeLanguage The signal for the active language. For `I18n<Translations>` this must be a writable signal,
   *                       but by extending `I18n<Translations, Signal<string>>` it can be readonly (e.g. computed).
   * @param interceptor A callback that is called after each translation request, used for testing.
   */
  protected constructor(
    private readonly loader: TranslationLoader,
    public readonly activeLanguage: S,
    private readonly interceptor?: I18nInterceptor,
  ) {}

  readonly translations: Signal<Translation | Record<string, never>> = toSignal(
    toObservable(this.activeLanguage).pipe(
      startWith(this.activeLanguage()), // Needed so that the request starts synchronously
      distinctUntilChanged(),
      switchMap((lang) => this.loader.getTranslations(lang)),
    ),
    { initialValue: {} },
  );

  private lookup(querySegments: string[], items: TranslationValue, i = 0): TranslationValue | undefined {
    if (querySegments.length <= i) {
      return items;
    } else if (typeof items === 'string') {
      return undefined;
    }
    const next = items[querySegments[i]];
    if (next === undefined) {
      return next;
    }
    return this.lookup(querySegments, next, i + 1);
  }

  private getTranslation(query: string) {
    return computed(() => {
      const querySegments = query.split('.');
      const translation = this.lookup(querySegments, this.translations());
      this.interceptor?.notifyTranslated(query, !translation);
      return translation;
    });
  }

  private translationAsString(value: TranslationValue | undefined) {
    return typeof value === 'string' ? value : '';
  }

  use(): UseI18n<T> {
    const cache: Record<string, Signal<TranslationValue | undefined>> = {};
    const formatterCache: Record<string, Signal<IntlMessageFormat>> = {};

    const translateWithCache = (key: string): Signal<TranslationValue | undefined> => {
      cache[key] ??= this.getTranslation(key);
      return cache[key];
    };

    const translateInterpolated = (key: KeyPathsOfType<T, string>, values?: Record<string, PrimitiveType>): string => {
      const cachedTranslationSignal = translateWithCache(key);
      if (!values) {
        return this.translationAsString(cachedTranslationSignal());
      }

      const cachedFormatter = (formatterCache[key] ??= computed(
        () => new IntlMessageFormat(this.translationAsString(cachedTranslationSignal()), this.activeLanguage()),
      ));
      return cachedFormatter().format(values) as string;
    };

    const translateObject = <Type extends Record<string, string>>(path: KeyPathsOfType<T, Type>): Type => {
      return translateWithCache(path)() as Type;
    };

    const translateEnum = <EnumType extends string>(
      value: EnumType | undefined,
      enumKey: KeyPathsOfType<T, Record<EnumType, string>>,
      fallback = value as string,
    ): string => {
      if (value === undefined) return fallback;
      return translateObject<Record<EnumType, string>>(enumKey)?.[value] ?? fallback;
    };

    const translatePartialEnum = <EnumType extends string>(
      value: EnumType | undefined,
      enumKey: KeyPathsOfType<T, Record<string, string>>,
      fallback = value as string,
    ): string => {
      if (value === undefined) return fallback;
      return translateObject<Record<string, string>>(enumKey)?.[value] ?? fallback;
    };

    const makeDateFormatter = (format: Intl.DateTimeFormatOptions) => (date: Date) => {
      const formatter = new Intl.DateTimeFormat(this.activeLanguage(), format);

      return formatter.format(date);
    };

    // Return a callable i18n object, supporting i18n(), i18n.enum(), i18n.partialEnum() and i18n.object()
    return Object.assign(translateInterpolated, {
      enum: translateEnum,
      partialEnum: translatePartialEnum,
      object: translateObject,

      date: makeDateFormatter({ dateStyle: 'short' }),
      datetime: makeDateFormatter({ dateStyle: 'short', timeStyle: 'short' }),
      time: makeDateFormatter({ timeStyle: 'short' }),
    });
  }
}
